import { useApp } from '@/contexts';

import { useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ListIcon from '@mui/icons-material/List';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { type GridPaginationModel } from '@mui/x-data-grid';

import { useSnackbar } from '@/components/feedback/SnackbarProvider';
import { PageContainer, PageHeading } from '@/components/layout';
import { useGetEnvironmentsQuery } from '@/features/environments/api/environmentsApi';
import {
  useClearTriggerMutation,
  useGetAppDeploymentsQuery,
  useGetTransformersQuery,
  useGetTriggerDetailsQuery,
  useGetTriggerSummaryQuery,
  usePauseEnvironmentSchedulesMutation,
  useSaveTriggerMutation,
} from '@/features/transformers/api/transformersApi';
import type {
  CreateTriggerRequest,
  DateRangePreset,
  DeploymentOption,
  ScheduleCalendarEvent,
  ScheduleSummary,
  TriggerDetail,
} from '@/types/schedule';
import { logger } from '@/utils/logger';

import { CreateScheduleDialog } from '../components/CreateScheduleDialog';
import { EditScheduleDialog } from '../components/EditScheduleDialog';
import { PlannedUpcomingGrid } from '../components/PlannedUpcomingGrid';
import { ScheduleCalendarView } from '../components/ScheduleCalendarView';
import { ScheduleDetailsGrid } from '../components/ScheduleDetailsGrid';
import { ScheduleSummaryGrid } from '../components/ScheduleSummaryGrid';
import { type TimeDisplayMode, TimeDisplayToggle } from '../components/shared/TimeDisplayToggle';
import { formatDateForApi, getDateRangeFromPreset } from '../utils/dateRanges';
import { getBrowserTimezone } from '../utils/timezoneData';
import { generateUpcomingScheduleRuns } from '../utils/upcomingScheduleRuns';

type ViewMode = 'list' | 'calendar';

export const SchedulesPage = () => {
  const { appId, tenantId } = useApp();
  const { showSnackbar } = useSnackbar();

  // Local state
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState<string>('');
  // TODO: "Show Unscheduled" toggle — state is set but never consumed. Originally
  // intended to merge deployed-but-unscheduled recipes into the summary grid
  // (parity with legacy dataProtect). Revisit and either wire it up or remove.
  // const [showUnscheduled, setShowUnscheduled] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<ScheduleSummary | null>(null);

  // Pagination for details grid
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20,
  });
  const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('today');
  const [scheduledDateRangePreset, setScheduledDateRangePreset] =
    useState<DateRangePreset>('thisWeek');
  const [timeMode, setTimeMode] = useState<TimeDisplayMode>('local');
  const [showUpcomingScheduleList, setShowUpcomingScheduleList] = useState(false);
  const [showUpcomingScheduleCalendar, setShowUpcomingScheduleCalendar] = useState(false);
  const [plannedPaginationModel, setPlannedPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20,
  });

  // Separate date range for calendar view (updated on navigation)
  const [calendarDateRange, setCalendarDateRange] = useState<{
    startDate: Date;
    endDate: Date;
  } | null>(null);

  // Get date range for API call
  const dateRange = useMemo(() => getDateRangeFromPreset(dateRangePreset), [dateRangePreset]);
  const scheduledDateRange = useMemo(
    () => getDateRangeFromPreset(scheduledDateRangePreset),
    [scheduledDateRangePreset]
  );

  // API queries — track both isLoading (first load) and isFetching (refetches)
  const { data: environments, isLoading: environmentsLoading } = useGetEnvironmentsQuery(
    { tenantId, appId: appId || '' },
    { skip: !appId || !tenantId }
  );

  const {
    data: triggerSummary,
    isLoading: summaryLoading,
    isFetching: summaryFetching,
    refetch: refetchSummary,
  } = useGetTriggerSummaryQuery(
    {
      tenantId,
      appId: appId || '',
      timeZone: getBrowserTimezone(),
      envId: selectedEnvironmentId || undefined,
    },
    { skip: !appId }
  );

  // Effective date range for ES history: list view uses the history preset, calendar uses its own range
  const effectiveDateRange = useMemo(() => {
    if (viewMode === 'calendar' && calendarDateRange) {
      return calendarDateRange;
    }
    return dateRange;
  }, [viewMode, calendarDateRange, dateRange]);

  const effectiveScheduledDateRange = useMemo(() => {
    if (viewMode === 'calendar' && calendarDateRange) {
      return calendarDateRange;
    }
    return scheduledDateRange;
  }, [viewMode, calendarDateRange, scheduledDateRange]);

  const {
    data: triggerDetails,
    isLoading: detailsLoading,
    isFetching: detailsFetching,
    refetch: refetchDetails,
  } = useGetTriggerDetailsQuery(
    {
      tenantId,
      appId: appId || '',
      startDate: formatDateForApi(effectiveDateRange.startDate),
      endDate: formatDateForApi(effectiveDateRange.endDate),
      envId: selectedEnvironmentId || undefined,
      timeZone: viewMode === 'calendar' ? getBrowserTimezone() : 'UTC',
      currentPage: viewMode === 'list' ? paginationModel.page + 1 : 0,
      pageSize: viewMode === 'list' ? paginationModel.pageSize : 0,
    },
    { skip: !appId }
  );

  const { data: deployments, isLoading: deploymentsLoading } = useGetAppDeploymentsQuery(
    { tenantId, appId: appId || '' },
    { skip: !appId || !tenantId }
  );

  const { data: transformers = [] } = useGetTransformersQuery(appId || '', {
    skip: !appId,
  });

  // Mutations
  const [saveTrigger] = useSaveTriggerMutation();
  const [clearTrigger] = useClearTriggerMutation();
  const [pauseEnvironment, { isLoading: isPausingResuming }] =
    usePauseEnvironmentSchedulesMutation();

  // Computed values
  const schedules = useMemo(() => {
    if (!triggerSummary) return [];
    // Cast to ScheduleSummary[] - API response matches this type
    return triggerSummary as unknown as ScheduleSummary[];
  }, [triggerSummary]);

  const details = useMemo(() => {
    if (!triggerDetails) return { items: [], total: 0 };

    return {
      items: (triggerDetails.triggerDetails || []) as TriggerDetail[],
      total: triggerDetails.totalRecordCount ?? 0,
    };
  }, [triggerDetails]);

  const showUpcomingSchedule =
    viewMode === 'calendar' ? showUpcomingScheduleCalendar : showUpcomingScheduleList;

  const plannedDetails = useMemo(() => {
    if (!showUpcomingSchedule) {
      return [];
    }

    return generateUpcomingScheduleRuns(
      schedules,
      effectiveScheduledDateRange.startDate,
      effectiveScheduledDateRange.endDate
    );
  }, [showUpcomingSchedule, schedules, effectiveScheduledDateRange]);

  const calendarExecutionDetails = useMemo(() => {
    const items = details.items as TriggerDetail[];
    if (showUpcomingScheduleCalendar) {
      return items;
    }
    return items.filter((detail) => detail.executionStatus !== 'Scheduled');
  }, [details.items, showUpcomingScheduleCalendar]);

  const activeScheduleCount = useMemo(() => {
    return schedules.filter((s) => !s.isSchedulesPaused).length;
  }, [schedules]);

  const pausedEnvironmentCount = useMemo(() => {
    const pausedEnvIds = new Set(
      schedules.filter((s) => s.isSchedulesPaused).map((s) => s.environmentId)
    );
    return pausedEnvIds.size;
  }, [schedules]);

  // Check if selected environment is paused (from environments data, matching legacy behavior)
  const isSelectedEnvironmentPaused = useMemo(() => {
    if (!selectedEnvironmentId || !environments) return false;
    const env = environments.find((e) => String(e.id) === String(selectedEnvironmentId));
    return env?.pauseSchedules ?? false;
  }, [environments, selectedEnvironmentId]);

  // Handlers
  const handleViewModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: ViewMode | null
  ) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const handleEnvironmentChange = (event: SelectChangeEvent) => {
    setSelectedEnvironmentId(event.target.value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setPlannedPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // TODO: paired with the commented-out `showUnscheduled` state above — re-enable
  // once we decide whether to wire this toggle to filter/merge unscheduled deployments.
  // const handleToggleUnscheduled = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   setShowUnscheduled(event.target.checked);
  // };

  const handlePauseResume = async () => {
    if (!selectedEnvironmentId || !appId) return;

    const action = isSelectedEnvironmentPaused ? 'resumed' : 'paused';
    try {
      await pauseEnvironment({
        tenantId,
        appId,
        envId: selectedEnvironmentId,
        isPaused: !isSelectedEnvironmentPaused,
      }).unwrap();

      refetchSummary();
      showSnackbar(`Schedules ${action} successfully`, 'success');
    } catch (error) {
      logger.error('Failed to pause/resume environment:', error);
      showSnackbar(
        `Failed to ${isSelectedEnvironmentPaused ? 'resume' : 'pause'} schedules`,
        'error'
      );
    }
  };

  const handleCreateSchedule = async (data: CreateTriggerRequest) => {
    await saveTrigger({ tenantId, data }).unwrap();
    await Promise.all([refetchSummary(), refetchDetails()]);
    showSnackbar('Schedule created successfully', 'success');
  };

  const handleEditSchedule = async (data: CreateTriggerRequest) => {
    await saveTrigger({ tenantId, data }).unwrap();
    await Promise.all([refetchSummary(), refetchDetails()]);
    showSnackbar('Schedule updated successfully', 'success');
  };

  const handleDeleteSchedule = async () => {
    if (!selectedSchedule) return;
    await clearTrigger({
      envId: String(selectedSchedule.environmentId),
      transBaseId: String(selectedSchedule.baseId),
    }).unwrap();
    await Promise.all([refetchSummary(), refetchDetails()]);
    setSelectedSchedule(null);
    showSnackbar('Schedule deleted successfully', 'success');
  };

  const handleEditClick = (schedule: ScheduleSummary) => {
    setSelectedSchedule(schedule);
    setEditDialogOpen(true);
  };

  const handleCalendarDateChange = (start: Date, end: Date) => {
    setCalendarDateRange({ startDate: start, endDate: end });
  };

  const handleCalendarEventClick = (event: ScheduleCalendarEvent) => {
    // Legacy behavior: if the event has an externalExecutionId (already ran),
    // navigate to health details instead of opening edit dialog
    if (event.resource?.externalExecutionId && appId) {
      window.open(
        `/a/${appId}/health?executionGuid=${event.resource.externalExecutionId}&env=${event.resource.environmentId}`,
        '_blank'
      );
      return;
    }

    // Otherwise open the edit dialog by matching the summary row
    const schedule = schedules.find(
      (s) =>
        String(s.baseId) === String(event.resource?.baseId) &&
        String(s.environmentId) === String(event.resource?.environmentId)
    );
    if (schedule) {
      setSelectedSchedule(schedule);
      setEditDialogOpen(true);
    }
  };

  const handleDateRangeChange = (preset: DateRangePreset) => {
    setDateRangePreset(preset);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setPlannedPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleScheduledDateRangeChange = (preset: DateRangePreset) => {
    setScheduledDateRangePreset(preset);
    setPlannedPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handlePaginationChange = (model: GridPaginationModel) => {
    setPaginationModel(model);
  };

  const handlePlannedPaginationChange = (model: GridPaginationModel) => {
    setPlannedPaginationModel(model);
  };

  const handleShowUpcomingScheduleListChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowUpcomingScheduleList(event.target.checked);
    setPlannedPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleShowUpcomingScheduleCalendarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowUpcomingScheduleCalendar(event.target.checked);
  };

  // Enrich deployments with transformer and environment names (version lookup must not
  // wait for transformers/environments — schedules can render before those resolve).
  const deploymentOptions: DeploymentOption[] = useMemo(() => {
    if (!deployments) return [];

    return deployments.map((deployment) => {
      const transformer = transformers?.find((t) => String(t.baseId) === String(deployment.baseId));
      const environment = environments?.find(
        (e) => String(e.id) === String(deployment.environmentId)
      );

      return {
        ...deployment,
        transformerName:
          transformer?.name || deployment.transformerName || `Recipe ${deployment.baseId}`,
        environmentName:
          environment?.name || deployment.environmentName || `Env ${deployment.environmentId}`,
        processingType: transformer?.processingType,
      };
    });
  }, [deployments, transformers, environments]);

  // First load blocks the entire page; subsequent fetches show inline indicators
  const isInitialLoad = summaryLoading && detailsLoading && environmentsLoading;

  const getPauseResumeIcon = () => {
    if (isPausingResuming) return <CircularProgress size={18} color="inherit" />;
    if (isSelectedEnvironmentPaused) return <PlayArrowIcon />;
    return <PauseIcon />;
  };

  return (
    <PageContainer>
      <PageHeading
        title="Schedules"
        subtitle="Manage automated recipe runs and scheduling"
        action={
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            {/* Active count indicators */}
            {!summaryLoading && (
              <>
                {activeScheduleCount > 0 && (
                  <Chip
                    label={`${activeScheduleCount} Active`}
                    size="small"
                    color="success"
                    sx={{ fontWeight: 500 }}
                  />
                )}
                {pausedEnvironmentCount > 0 && (
                  <Chip
                    label={`${pausedEnvironmentCount} Env${pausedEnvironmentCount > 1 ? 's' : ''} Paused`}
                    size="small"
                    color="warning"
                  />
                )}
              </>
            )}
            {summaryFetching && !summaryLoading && <CircularProgress size={18} thickness={4} />}

            {/* View Toggle */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
            >
              <ToggleButton value="list">
                <ListIcon sx={{ mr: 0.5 }} fontSize="small" />
                List
              </ToggleButton>
              <ToggleButton value="calendar">
                <CalendarTodayIcon sx={{ mr: 0.5 }} fontSize="small" />
                Calendar
              </ToggleButton>
            </ToggleButtonGroup>

            <TimeDisplayToggle value={timeMode} onChange={setTimeMode} />

            {/* New Schedule Button */}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              New Schedule
            </Button>
          </Stack>
        }
      />

      {/* Filters Row */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          {/* Environment Filter */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Environment</InputLabel>
            <Select
              value={selectedEnvironmentId}
              onChange={handleEnvironmentChange}
              label="Environment"
              disabled={environmentsLoading}
            >
              <MenuItem value="">All Environments</MenuItem>
              {environments?.map((env) => (
                <MenuItem key={env.id} value={env.id}>
                  {env.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {environmentsLoading && <CircularProgress size={20} />}

          {/* Pause/Resume Button (only when environment is selected) */}
          {selectedEnvironmentId && (
            <Button
              variant="outlined"
              color={isSelectedEnvironmentPaused ? 'success' : 'warning'}
              startIcon={getPauseResumeIcon()}
              onClick={handlePauseResume}
              disabled={isPausingResuming}
            >
              {isPausingResuming
                ? 'Updating...'
                : `${isSelectedEnvironmentPaused ? 'Resume' : 'Pause'} Schedules`}
            </Button>
          )}

          {/* TODO: "Show Unscheduled" toggle — hidden until we wire it to filter logic.
              See commented `showUnscheduled` state / handler above. */}
          {/* <FormControlLabel
            control={<Switch checked={showUnscheduled} onChange={handleToggleUnscheduled} />}
            label="Show Unscheduled"
          /> */}
        </Stack>
      </Paper>

      {/* Initial full-page loading state (first load only) */}
      {isInitialLoad && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Content — always rendered after initial load; grids handle their own loading */}
      {!isInitialLoad &&
        (viewMode === 'list' ? (
          <Stack spacing={3}>
            {/* Summary Grid */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Scheduled Recipes
              </Typography>
              <ScheduleSummaryGrid
                schedules={schedules}
                isLoading={summaryFetching}
                onEdit={handleEditClick}
                onRefresh={refetchSummary}
                selectedEnvironmentId={selectedEnvironmentId || undefined}
                appId={appId}
                timeMode={timeMode}
                showTimeToggle={false}
                deployments={deploymentOptions}
              />
            </Paper>

            {/* Details Grid */}
            <Paper sx={{ p: 2 }}>
              <ScheduleDetailsGrid
                details={details.items as TriggerDetail[]}
                totalCount={details.total}
                isLoading={detailsFetching}
                paginationModel={paginationModel}
                onPaginationChange={handlePaginationChange}
                dateRangePreset={dateRangePreset}
                onDateRangeChange={handleDateRangeChange}
                onRefresh={refetchDetails}
                appId={appId}
                schedules={schedules}
              />
              <Stack spacing={1} sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showUpcomingScheduleList}
                      onChange={handleShowUpcomingScheduleListChange}
                      size="small"
                    />
                  }
                  label="Show upcoming schedules"
                />
                {!showUpcomingScheduleList && (
                  <Typography variant="caption" color="text.secondary">
                    Enable to preview upcoming fire times from active schedules.
                  </Typography>
                )}
              </Stack>
              {showUpcomingScheduleList && (
                <Box sx={{ mt: 2 }}>
                  <PlannedUpcomingGrid
                    details={plannedDetails}
                    isLoading={summaryFetching}
                    paginationModel={plannedPaginationModel}
                    onPaginationChange={handlePlannedPaginationChange}
                    dateRangePreset={scheduledDateRangePreset}
                    onDateRangeChange={handleScheduledDateRangeChange}
                    schedules={schedules}
                  />
                </Box>
              )}
            </Paper>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Paper sx={{ px: 2, py: 1.5 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showUpcomingScheduleCalendar}
                    onChange={handleShowUpcomingScheduleCalendarChange}
                    size="small"
                  />
                }
                label="Show upcoming schedules"
              />
              {!showUpcomingScheduleCalendar && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Enable to preview upcoming fire times from active schedules on the calendar.
                </Typography>
              )}
            </Paper>
            <ScheduleCalendarView
              schedules={schedules}
              executionDetails={calendarExecutionDetails}
              plannedExecutionDetails={plannedDetails}
              showUpcomingSchedules={showUpcomingScheduleCalendar}
              isLoading={summaryFetching || detailsFetching}
              onEventClick={handleCalendarEventClick}
              onDateChange={handleCalendarDateChange}
              selectedEnvironmentId={selectedEnvironmentId || undefined}
              timeMode={timeMode}
            />
          </Stack>
        ))}

      {/* Empty State — only show when data has loaded and is genuinely empty */}
      {!summaryLoading && !summaryFetching && schedules.length === 0 && !isInitialLoad && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary" gutterBottom>
            No schedules found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a schedule to automatically run your recipes on a regular basis.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Your First Schedule
          </Button>
        </Paper>
      )}

      {/* Create Dialog */}
      <CreateScheduleDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateSchedule}
        deployments={deploymentOptions}
        deploymentsLoading={deploymentsLoading}
        tenantId={tenantId}
      />

      {/* Edit Dialog */}
      <EditScheduleDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedSchedule(null);
        }}
        onSubmit={handleEditSchedule}
        onDelete={handleDeleteSchedule}
        schedule={selectedSchedule}
      />
    </PageContainer>
  );
};
